import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Rendering failure caught', error, info);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(previousProps: ErrorBoundaryProps): void {
    if (
      this.state.hasError
      && previousProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex h-full flex-col items-center justify-center bg-gray-50 p-8">
        <div className="max-w-md space-y-4 text-center">
          <h2 className="text-lg font-semibold text-gray-800">
            The map view needs to be refreshed
          </h2>
          <p className="text-sm text-gray-600">
            The browser changed part of the page while the map was updating.
            Refreshing restores the planner without changing saved project data.
          </p>
          <p className="break-all font-mono text-xs text-gray-400">
            {this.state.error?.message}
          </p>
          <button
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            onClick={() => window.location.reload()}
          >
            Refresh planner
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
